import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type HTMLAttributes, type ReactNode } from 'react';

import { type AllowedDisplayElements } from '@repo/ui/utils/html';
import {
  type MarginUtilityClass,
  type PaddingUtilityClass,
} from '@repo/ui/utils/spacing';

import s from './Box.module.css';

export const boxDisplayPropertyOptions = [
  'block',
  'inline',
  'inline-block',
] as const;
export type BoxDisplayProperties = (typeof boxDisplayPropertyOptions)[number];

type BoxHTMLAttributes<T extends AllowedDisplayElements> = HTMLAttributes<
  Extract<HTMLElementTagNameMap[T], HTMLElement>
>;

interface BoxProps<T extends AllowedDisplayElements = AllowedDisplayElements>
  extends BoxHTMLAttributes<T> {
  as?: T;
  children: ReactNode;
  className?: string;
  isContainer?: boolean;
  display?: BoxDisplayProperties;
  isFlex?: boolean;
  margin?: MarginUtilityClass | MarginUtilityClass[];
  padding?: PaddingUtilityClass | PaddingUtilityClass[];
}

export const boxVariants = cva(s.box, {
  variants: {
    display: {
      block: s.displayBlock,
      inline: s.displayInline,
      ['inline-block']: s.displayInlineBlock,
    },
    isContainer: {
      true: s.isContainer,
    },
    isFlex: {
      true: s.isFlex,
    },
    // the margin and padding variants are not implemented as boxVariants, as they use utility classes and not CSS modules.
  },
  defaultVariants: {},
});

export const Box = (props: BoxProps) => {
  const {
    as,
    children,
    className,
    isContainer = false,
    display = 'block',
    isFlex = false,
    margin,
    padding,
    ...attrs
  } = props;

  if (!children) {
    return null;
  }

  const Component = as ?? 'div';

  return (
    <Component
      className={clsx(
        boxVariants({
          className,
          isContainer,
          display,
          isFlex,
        }),
        margin,
        padding,
      )}
      {...attrs}
    >
      {children}
    </Component>
  );
};

Box.displayName = 'Box';
